import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ThemeName } from '../theme/tokens';
import type { FooterFilter } from '../utils/footerFilter';
import type { MarketId } from '../components/common/LockedState';
import type { SentimentTimeframe } from '../api/restApi';
import { setPrivacyMask } from '../utils/tradeMetrics';

export type TradeMode = 'paper' | 'live';

export const MAX_CHART_TABS = 8;

export const SENTIMENT_TIMEFRAMES: ReadonlyArray<SentimentTimeframe> =
  ['1h', '1d', '7d', '1m', '3m', '1y'] as const;

type SettingsState = {
  theme: ThemeName;
  sidebarCollapsed: boolean;
  disclaimerAcceptedAt: string | null;
  selectedSymbol: string | null;
  selectedMarket: MarketId;
  /** Ordered list of symbol codes open as chart tabs (max 8). */
  chartTabs: string[];
  /** The currently-active tab symbol code, or null when no tabs open. */
  activeChartTab: string | null;
  /** Which mode the Strategy + Chart surfaces activate/execute against.
   *  Defaults to 'paper' — users can promote individual strategies to
   *  live via the LiveTradingSetupModal once their prereqs are met. */
  tradeMode: TradeMode;
  chartInterval: number;
  footerQuoteFilter: FooterFilter;
  enabledEmas: number[];
  showRsi: boolean;
  enabledSmas: number[];
  showBbands: boolean;
  showMacd: boolean;
  showVwap: boolean;
  showAdx: boolean;
  showAtr: boolean;
  showCmf: boolean;
  showIchimoku: boolean;
  showObv: boolean;
  showPsar: boolean;
  showPivot: boolean;
  showStoch: boolean;
  showStochRsi: boolean;
  showWilliamsr: boolean;
  showFvg: boolean;
  showBos: boolean;
  showChoch: boolean;
  showFib: boolean;
  showSvp: boolean;
  // Trade-flow indicators (derived from the trade_flow_* aggregates).
  showSignedVolume: boolean;
  showTradeImbalance: boolean;
  showLargeTrades: boolean;
  // Ticket 054 — geometry + forecast overlays
  showTrendlines: boolean;
  showPatterns: boolean;
  showTrendState: boolean;
  showForecastDet: boolean;
  showForecastMl: boolean;
  // Support / resistance levels — driven by SVP (value-area + POC) and
  // by live book liquidity (top-% resting walls). See ticket 054.
  showSrVolume: boolean;
  showSrLiquidity: boolean;
  showSrCandles: boolean;
  showLiquidityHeatmap: boolean;
  showBookmap: boolean;
  /** Sentiment overlay on chart pane (050). */
  showSentimentOverlay: boolean;
  /** Sentiment page timeframe selector (051) — also feeds 050 overlay window. */
  sentimentTimeframe: SentimentTimeframe;
  /** Privacy ("streamer") mode — masks every USD value as ******* app-wide. */
  privacyMode: boolean;
  // Notification preferences
  notifyTradeAlerts: boolean;
  notifySignalAlerts: boolean;
  notifyDrawdownWarnings: boolean;
  notifyDailySummary: boolean;
  setTheme: (theme: ThemeName) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  acceptDisclaimer: (acceptedAt?: string) => void;
  setSelectedSymbol: (symbol: string | null) => void;
  setSelectedMarket: (m: MarketId) => void;
  openChartTab: (symbol: string) => void;
  closeChartTab: (symbol: string) => void;
  setActiveChartTab: (symbol: string | null) => void;
  setTradeMode: (mode: TradeMode) => void;
  setChartInterval: (interval: number) => void;
  setFooterQuoteFilter: (filter: FooterFilter) => void;
  toggleEma: (period: number) => void;
  toggleRsi: () => void;
  toggleSma: (period: number) => void;
  toggleBbands: () => void;
  toggleMacd: () => void;
  toggleVwap: () => void;
  toggleAdx: () => void;
  toggleAtr: () => void;
  toggleCmf: () => void;
  toggleIchimoku: () => void;
  toggleObv: () => void;
  togglePsar: () => void;
  togglePivot: () => void;
  toggleStoch: () => void;
  toggleStochRsi: () => void;
  toggleWilliamsr: () => void;
  toggleFvg: () => void;
  toggleBos: () => void;
  toggleChoch: () => void;
  toggleFib: () => void;
  toggleSvp: () => void;
  toggleSignedVolume: () => void;
  toggleTradeImbalance: () => void;
  toggleLargeTrades: () => void;
  toggleTrendlines: () => void;
  togglePatterns: () => void;
  toggleTrendState: () => void;
  toggleForecastDet: () => void;
  toggleForecastMl: () => void;
  toggleSrVolume: () => void;
  toggleSrLiquidity: () => void;
  toggleSrCandles: () => void;
  toggleLiquidityHeatmap: () => void;
  toggleBookmap: () => void;
  toggleSentimentOverlay: () => void;
  setSentimentTimeframe: (tf: SentimentTimeframe) => void;
  togglePrivacyMode: () => void;
  setNotifyTradeAlerts: (on: boolean) => void;
  setNotifySignalAlerts: (on: boolean) => void;
  setNotifyDrawdownWarnings: (on: boolean) => void;
  setNotifyDailySummary: (on: boolean) => void;
};

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      theme: 'dark',
      sidebarCollapsed: false,
      disclaimerAcceptedAt: null,
      selectedSymbol: null,
      selectedMarket: 'spot',
      chartTabs: [],
      activeChartTab: null,
      tradeMode: 'paper',
      chartInterval: 1,
      footerQuoteFilter: 'USD',
      enabledEmas: [21],
      showRsi: true,
      enabledSmas: [],
      showBbands: false,
      showMacd: false,
      showVwap: false,
      showAdx: false,
      showAtr: false,
      showCmf: false,
      showIchimoku: false,
      showObv: false,
      showPsar: false,
      showPivot: false,
      showStoch: false,
      showStochRsi: false,
      showWilliamsr: false,
      showFvg: false,
      showBos: false,
      showChoch: false,
      showFib: false,
      showSvp: false,
      showSignedVolume: false,
      showTradeImbalance: false,
      showLargeTrades: false,
      showTrendlines: false,
      showPatterns: false,
      showTrendState: false,
      showForecastDet: false,
      showForecastMl: false,
      showSrVolume: false,
      showSrLiquidity: false,
      showSrCandles: false,
      showLiquidityHeatmap: true,
      showBookmap: true,
      showSentimentOverlay: false,
      sentimentTimeframe: '1d',
      privacyMode: false,
      // All notification categories default-on — traders want signal flow
      // unless they explicitly opt out. Changed from partial-on to all-on.
      notifyTradeAlerts: true,
      notifySignalAlerts: true,
      notifyDrawdownWarnings: true,
      notifyDailySummary: true,
      setTheme: (theme) => set({ theme }),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      acceptDisclaimer: (acceptedAt) =>
        set({ disclaimerAcceptedAt: acceptedAt ?? new Date().toISOString() }),
      setSelectedSymbol: (selectedSymbol) => set({ selectedSymbol }),
      setSelectedMarket: (selectedMarket) => set({ selectedMarket }),
      openChartTab: (symbol) =>
        set((state) => {
          if (state.chartTabs.includes(symbol)) {
            return { activeChartTab: symbol };
          }
          if (state.chartTabs.length >= MAX_CHART_TABS) return {};
          return { chartTabs: [...state.chartTabs, symbol], activeChartTab: symbol };
        }),
      closeChartTab: (symbol) =>
        set((state) => {
          const tabs = state.chartTabs.filter((s) => s !== symbol);
          const active =
            state.activeChartTab === symbol
              ? (tabs[tabs.length - 1] ?? null)
              : state.activeChartTab;
          return { chartTabs: tabs, activeChartTab: active };
        }),
      setActiveChartTab: (activeChartTab) => set({ activeChartTab }),
      setTradeMode: (tradeMode) => set({ tradeMode }),
      setChartInterval: (chartInterval) => set({ chartInterval }),
      setFooterQuoteFilter: (footerQuoteFilter) => set({ footerQuoteFilter }),
      toggleEma: (period) =>
        set((state) => ({
          enabledEmas: state.enabledEmas.includes(period)
            ? state.enabledEmas.filter((p) => p !== period)
            : [...state.enabledEmas, period],
        })),
      toggleRsi: () => set((state) => ({ showRsi: !state.showRsi })),
      toggleSma: (period) =>
        set((state) => ({
          enabledSmas: state.enabledSmas.includes(period)
            ? state.enabledSmas.filter((p) => p !== period)
            : [...state.enabledSmas, period],
        })),
      toggleBbands: () => set((state) => ({ showBbands: !state.showBbands })),
      toggleMacd: () => set((state) => ({ showMacd: !state.showMacd })),
      toggleVwap: () => set((state) => ({ showVwap: !state.showVwap })),
      toggleAdx: () => set((state) => ({ showAdx: !state.showAdx })),
      toggleAtr: () => set((state) => ({ showAtr: !state.showAtr })),
      toggleCmf: () => set((state) => ({ showCmf: !state.showCmf })),
      toggleIchimoku: () => set((state) => ({ showIchimoku: !state.showIchimoku })),
      toggleObv: () => set((state) => ({ showObv: !state.showObv })),
      togglePsar: () => set((state) => ({ showPsar: !state.showPsar })),
      togglePivot: () => set((state) => ({ showPivot: !state.showPivot })),
      toggleStoch: () => set((state) => ({ showStoch: !state.showStoch })),
      toggleStochRsi: () => set((state) => ({ showStochRsi: !state.showStochRsi })),
      toggleWilliamsr: () => set((state) => ({ showWilliamsr: !state.showWilliamsr })),
      toggleFvg: () => set((state) => ({ showFvg: !state.showFvg })),
      toggleBos: () => set((state) => ({ showBos: !state.showBos })),
      toggleChoch: () => set((state) => ({ showChoch: !state.showChoch })),
      toggleFib: () => set((state) => ({ showFib: !state.showFib })),
      toggleSvp: () => set((state) => ({ showSvp: !state.showSvp })),
      toggleSignedVolume: () => set((state) => ({ showSignedVolume: !state.showSignedVolume })),
      toggleTradeImbalance: () => set((state) => ({ showTradeImbalance: !state.showTradeImbalance })),
      toggleLargeTrades: () => set((state) => ({ showLargeTrades: !state.showLargeTrades })),
      toggleTrendlines: () => set((state) => ({ showTrendlines: !state.showTrendlines })),
      togglePatterns: () => set((state) => ({ showPatterns: !state.showPatterns })),
      toggleTrendState: () => set((state) => ({ showTrendState: !state.showTrendState })),
      toggleForecastDet: () => set((state) => ({ showForecastDet: !state.showForecastDet })),
      toggleForecastMl: () => set((state) => ({ showForecastMl: !state.showForecastMl })),
      toggleSrVolume: () => set((state) => ({ showSrVolume: !state.showSrVolume })),
      toggleSrLiquidity: () => set((state) => ({ showSrLiquidity: !state.showSrLiquidity })),
      toggleSrCandles: () => set((state) => ({ showSrCandles: !state.showSrCandles })),
      toggleLiquidityHeatmap: () => set((state) => ({ showLiquidityHeatmap: !state.showLiquidityHeatmap })),
      toggleBookmap: () => set((state) => ({ showBookmap: !state.showBookmap })),
      toggleSentimentOverlay: () => set((state) => ({ showSentimentOverlay: !state.showSentimentOverlay })),
      setSentimentTimeframe: (sentimentTimeframe) => set({ sentimentTimeframe }),
      togglePrivacyMode: () =>
        set((state) => {
          const privacyMode = !state.privacyMode;
          setPrivacyMask(privacyMode);
          return { privacyMode };
        }),
      setNotifyTradeAlerts: (notifyTradeAlerts) => set({ notifyTradeAlerts }),
      setNotifySignalAlerts: (notifySignalAlerts) => set({ notifySignalAlerts }),
      setNotifyDrawdownWarnings: (notifyDrawdownWarnings) => set({ notifyDrawdownWarnings }),
      setNotifyDailySummary: (notifyDailySummary) => set({ notifyDailySummary }),
    }),
    {
      name: 'lynx-settings',
      version: 3,
      // v1 → v2: default all notification toggles ON.
      // v2 → v3: add chartTabs + activeChartTab (defaults fine; no migration needed).
      migrate: (persisted, fromVersion) => {
        const s = (persisted ?? {}) as Record<string, unknown>;
        if (fromVersion < 2) {
          delete s.notifyTradeAlerts;
          delete s.notifySignalAlerts;
          delete s.notifyDrawdownWarnings;
          delete s.notifyDailySummary;
        }
        return s as SettingsState;
      },
      partialize: (state) => ({
        theme: state.theme,
        sidebarCollapsed: state.sidebarCollapsed,
        disclaimerAcceptedAt: state.disclaimerAcceptedAt,
        selectedMarket: state.selectedMarket,
        tradeMode: state.tradeMode,
        chartTabs: state.chartTabs,
        activeChartTab: state.activeChartTab,
        chartInterval: state.chartInterval,
        footerQuoteFilter: state.footerQuoteFilter,
        enabledEmas: state.enabledEmas,
        showRsi: state.showRsi,
        enabledSmas: state.enabledSmas,
        showBbands: state.showBbands,
        showMacd: state.showMacd,
        showVwap: state.showVwap,
        showAdx: state.showAdx,
        showAtr: state.showAtr,
        showCmf: state.showCmf,
        showIchimoku: state.showIchimoku,
        showObv: state.showObv,
        showPsar: state.showPsar,
        showPivot: state.showPivot,
        showStoch: state.showStoch,
        showStochRsi: state.showStochRsi,
        showWilliamsr: state.showWilliamsr,
        showFvg: state.showFvg,
        showBos: state.showBos,
        showChoch: state.showChoch,
        showFib: state.showFib,
        showSvp: state.showSvp,
        showSignedVolume: state.showSignedVolume,
        showTradeImbalance: state.showTradeImbalance,
        showLargeTrades: state.showLargeTrades,
        showTrendlines: state.showTrendlines,
        showPatterns: state.showPatterns,
        showTrendState: state.showTrendState,
        showForecastDet: state.showForecastDet,
        showForecastMl: state.showForecastMl,
        showSrVolume: state.showSrVolume,
        showSrLiquidity: state.showSrLiquidity,
        showSrCandles: state.showSrCandles,
        showLiquidityHeatmap: state.showLiquidityHeatmap,
        showBookmap: state.showBookmap,
        showSentimentOverlay: state.showSentimentOverlay,
        sentimentTimeframe: state.sentimentTimeframe,
        privacyMode: state.privacyMode,
        notifyTradeAlerts: state.notifyTradeAlerts,
        notifySignalAlerts: state.notifySignalAlerts,
        notifyDrawdownWarnings: state.notifyDrawdownWarnings,
        notifyDailySummary: state.notifyDailySummary,
      }),
      // After persisted state loads, sync the module-level privacy flag so the
      // pure USD formatters mask immediately on refresh — before any toggle.
      onRehydrateStorage: () => (state) => {
        setPrivacyMask(state?.privacyMode ?? false);
      },
    },
  ),
);
